import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = '';  // Use relative URLs

const CollabRequestForm = ({ onClose, onSuccess, existingRequest = null }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    looking_for: [],
    genre: [],
    compensation: 'Negotiable',
    timeline: '',
    status: 'Open'
  });

  const LOOKING_FOR_OPTIONS = [
    'Artist', 'Writer', 'Colorist', 'Letterer',
    'Voice Actor', 'Animator', 'Music Producer', 'Editor', 'Other'
  ];

  const GENRE_OPTIONS = [
    'Action', 'Fantasy', 'Sci-Fi', 'Romance', 'Comedy',
    'Horror', 'Slice of Life', 'Mystery', 'Drama', 'Other'
  ];

  const COMPENSATION_OPTIONS = ['Paid', 'Revenue Share', 'Unpaid', 'Negotiable'];
  const STATUS_OPTIONS = ['Open', 'In Progress', 'Closed'];

  useEffect(() => {
    if (existingRequest) {
      setFormData({
        title: existingRequest.title || '',
        description: existingRequest.description || '',
        looking_for: existingRequest.looking_for || [],
        genre: existingRequest.genre || [],
        compensation: existingRequest.compensation || 'Negotiable',
        timeline: existingRequest.timeline || '',
        status: existingRequest.status || 'Open'
      });
    }
  }, [existingRequest]);

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Please enter a title');
      return;
    }
    
    if (!formData.description.trim()) {
      alert('Please enter a description');
      return;
    }
    
    if (formData.looking_for.length === 0) {
      alert('Please select at least one role you\'re looking for');
      return;
    }
    
    if (formData.genre.length === 0) {
      alert('Please select at least one genre');
      return;
    }

    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (existingRequest) {
        // Update existing request
        await axios.put(
          '/api/collab-requests/${existingRequest.id}',
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        // Create new request
        await axios.post(
          '/api/collab-requests',
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error submitting collab request:', error);
      alert(error.response?.data?.detail || 'Failed to submit collaboration request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">
            {existingRequest ? 'Edit Collaboration Request' : 'Create Collaboration Request'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Looking for Artist for Sci-Fi Comic"
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe your project and what you're looking for in a collaborator..."
              rows={6}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              maxLength={2000}
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length} / 2000 characters
            </p>
          </div>

          {/* Looking For */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              What help are you looking for? <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {LOOKING_FOR_OPTIONS.map(option => (
                <label
                  key={option}
                  className={`flex items-center space-x-2 bg-gray-800 px-4 py-3 rounded-lg border ${
                    formData.looking_for.includes(option)
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-gray-700'
                  } cursor-pointer hover:border-orange-500 transition-colors`}
                >
                  <input
                    type="checkbox"
                    checked={formData.looking_for.includes(option)}
                    onChange={() => handleCheckboxChange('looking_for', option)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
                  />
                  <span className="text-white text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Genre <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {GENRE_OPTIONS.map(option => (
                <label
                  key={option}
                  className={`flex items-center space-x-2 bg-gray-800 px-4 py-3 rounded-lg border ${
                    formData.genre.includes(option)
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-gray-700'
                  } cursor-pointer hover:border-purple-500 transition-colors`}
                >
                  <input
                    type="checkbox"
                    checked={formData.genre.includes(option)}
                    onChange={() => handleCheckboxChange('genre', option)}
                    className="w-4 h-4 text-purple-500 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-white text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Compensation */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Compensation <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.compensation}
              onChange={(e) => setFormData(prev => ({ ...prev, compensation: e.target.value }))}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {COMPENSATION_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Timeline (Optional)
            </label>
            <input
              type="text"
              value={formData.timeline}
              onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))}
              placeholder="e.g., 3-6 months, Ongoing, ASAP"
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              maxLength={50}
            />
          </div>

          {/* Status (only for edit) */}
          {existingRequest && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
            >
              {submitting ? 'Submitting...' : existingRequest ? 'Update Request' : 'Create Request'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollabRequestForm;
